import { francAll } from "franc";
import { iso6393To1 } from "iso-639-3";
import { useCallback } from "react";
import { useLanguage } from "mocks/LanguageHooks";
import { useFeatureFlag } from "hooks/FlagHooks";

// Minimum score gap between the top and second candidate required before we trust the detection.
// Below this threshold the trigram model doesn't have enough signal to distinguish languages
// reliably (common on short, title-cased, or bracket-heavy strings), so we fall back to
// undefined and let the page-level lang apply. Emitting a wrong lang tag is worse than none.
// 0.04 is calibrated to sit above the false-positive ceiling seen on English text
// misclassified as a related language (Scandinavian/Germanic), while for shorter text (<50 chars),
// we use a more conservative 0.2 threshold, as it is more likely to output a false positive match.
const LONG_CONFIDENCE_GAP = 0.04;
const SHORT_CONFIDENCE_GAP = 0.2;

export function useGetTextLang(): (
  content: string,
  isHtml?: boolean,
) => string | undefined {
  const { data: weglotEnabled } = useFeatureFlag("weglot");
  const [currentLang] = useLanguage();

  return useCallback(
    (content: string, isHtml?: boolean): string | undefined => {
      if (weglotEnabled && currentLang != "en") {
        return undefined;
      }

      let parsedContent = content;

      if (isHtml) {
        parsedContent = strip(parsedContent);
      }

      const candidates = francAll(parsedContent, { minLength: 10 });
      const topLang = candidates[0]?.[0] ?? "und";
      const topScore = candidates[0]?.[1] ?? 0;
      const secondScore = candidates[1]?.[1] ?? 0;

      const gap = topScore - secondScore;
      const min_gap =
        parsedContent.length < 50 ? SHORT_CONFIDENCE_GAP : LONG_CONFIDENCE_GAP;

      if (topLang === "und" || topLang === "eng" || gap < min_gap) {
        return undefined;
      }

      return iso6393To1[topLang] ?? topLang;
    },
    [weglotEnabled, currentLang],
  );
}

function strip(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent;
}

// Map a ReactNode to a string for language detection
export function childrenToString(node: React.ReactNode): string {
  if (typeof node === "string") {
    return node;
  }
  if (typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(childrenToString).join("");
  }
  // React elements (e.g. FormattedMessage) can't be inspected before render,
  // so return '', set no lang
  return "";
}
